import { useAuthStore } from '@/store/useAuthStore';
import { Feature } from '@/types/auth';

/**
 * Hook to check if the current user has access to a specific feature
 * @param feature - The feature to check access for
 * @returns boolean indicating if user has access
 */
export const useHasFeature = (feature: Feature): boolean => {
    return useAuthStore((state) => state.hasFeature(feature));
};

/**
 * Hook to get all features the current user has access to
 * @returns Array of features the user has access to
 */
export const useFeatures = (): Feature[] => {
    return useAuthStore((state) => state.getFeatures());
};

/**
 * Hook to check if user has access to multiple features (OR logic)
 * @param features - Array of features to check
 * @returns true if user has access to at least one of the features
 */
export const useHasAnyFeature = (features: Feature[]): boolean => {
    const hasFeature = useAuthStore((state) => state.hasFeature);
    return features.some((feature) => hasFeature(feature));
};

/**
 * Hook to check if user has access to all specified features (AND logic)
 * @param features - Array of features to check
 * @returns true if user has access to all features
 */
export const useHasAllFeatures = (features: Feature[]): boolean => {
    const hasFeature = useAuthStore((state) => state.hasFeature);
    return features.every((feature) => hasFeature(feature));
};
